import type { SiteFaviconDescriptor } from '@kiipu/app-protocol';

const MAX_BYTES = 5 * 1024 * 1024;
const MAX_ENTRIES = 500;
const MAX_AGE = 30 * 86400_000;
type Entry = {
  id: string;
  partition: string;
  kind: 'descriptor' | 'image';
  value: SiteFaviconDescriptor | Blob;
  time: number;
  bytes: number;
};
let connection: Promise<IDBDatabase | null> | undefined;
function database() {
  if (typeof indexedDB === 'undefined') return Promise.resolve(null);
  connection ??= new Promise<IDBDatabase | null>((resolve) => {
    const request = indexedDB.open('kiipu-site-favicons-v1', 1);
    request.onupgradeneeded = () => {
      request.result
        .createObjectStore('entries', { keyPath: 'id' })
        .createIndex('partition', 'partition');
    };
    request.onsuccess = () => {
      request.result.onversionchange = () => {
        request.result.close();
        connection = undefined;
      };
      resolve(request.result);
    };
    request.onerror = request.onblocked = () => resolve(null);
  });
  return connection;
}
function result<T>(request: IDBRequest<T>) {
  return new Promise<T>((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}
function completed(transaction: IDBTransaction) {
  return new Promise<void>((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = transaction.onabort = () => reject(transaction.error);
  });
}
/** Best-effort cache. Identity is supplied by the authenticated library, never by an image URL. */
export class SiteFaviconCache {
  async remove(partition: string, kind: Entry['kind'], key: string) {
    try {
      const db = await database();
      if (!db) return;
      const transaction = db.transaction('entries', 'readwrite');
      const done = completed(transaction);
      transaction.objectStore('entries').delete(`${partition}:${kind}:${key}`);
      await done;
    } catch {
      /* The active store still marks the descriptor for revalidation. */
    }
  }
  async descriptors(partition: string): Promise<SiteFaviconDescriptor[]> {
    try {
      const db = await database();
      if (!db) return [];
      const entries = await result<Entry[]>(
        db.transaction('entries').objectStore('entries').index('partition').getAll(partition),
      );
      return entries
        .filter((entry) => entry.kind === 'descriptor' && entry.time > Date.now() - MAX_AGE)
        .map((entry) => entry.value as SiteFaviconDescriptor);
    } catch {
      return [];
    }
  }
  async image(partition: string, path: string): Promise<Blob | null> {
    try {
      const db = await database();
      if (!db) return null;
      const entry = await result<Entry | undefined>(
        db.transaction('entries').objectStore('entries').get(`${partition}:image:${path}`),
      );
      return entry &&
        entry.time > Date.now() - MAX_AGE &&
        entry.kind === 'image' &&
        entry.value instanceof Blob
        ? entry.value
        : null;
    } catch {
      return null;
    }
  }
  async save(partition: string, kind: Entry['kind'], key: string, value: Entry['value']) {
    try {
      const db = await database();
      if (!db) return;
      const transaction = db.transaction('entries', 'readwrite');
      const done = completed(transaction);
      void done.catch(() => undefined);
      const store = transaction.objectStore('entries');
      const id = `${partition}:${kind}:${key}`;
      if (kind === 'descriptor') {
        const previous = await result<Entry | undefined>(store.get(id));
        if (
          previous &&
          (previous.value as SiteFaviconDescriptor).version >
            (value as SiteFaviconDescriptor).version
        ) {
          await done;
          return;
        }
      }
      store.put({
        id,
        partition,
        kind,
        value,
        time: Date.now(),
        bytes: value instanceof Blob ? value.size : JSON.stringify(value).length * 2,
      } satisfies Entry);
      const entries = await result<Entry[]>(store.getAll());
      let bytes = entries.reduce((total, entry) => total + entry.bytes, 0);
      let count = entries.length;
      for (const entry of entries.sort((a, b) => a.time - b.time)) {
        if (bytes <= MAX_BYTES && count <= MAX_ENTRIES && entry.time > Date.now() - MAX_AGE)
          continue;
        store.delete(entry.id);
        bytes -= entry.bytes;
        count--;
      }
      await done;
    } catch {
      /* Storage failure must not interfere with saving or reading content. */
    }
  }
}
